/** Shared bits for the C1 petition dialogs — field wrapper, money, file drop zone with chunked upload, court field. */
import { useRef, useState } from "react";
import { Landmark, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { legalUploadChunk, uploadInboxAttachment } from "@/lib/api";
import CourtPicker, { formatCourtAddress } from "@/pages/legal/CourtPicker";

export const today = () => new Date().toISOString().slice(0, 10);
export const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-GB") : "—");
export const errMsg = (e) => { const d = e?.response?.data?.detail; return (typeof d === "string" ? d : d?.message) || e.message; };
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block space-y-1 ${className}`}><span className="text-[11px] font-medium text-slate-600">{label}</span>{children}{hint && <span className="block text-[10px] text-muted-foreground">{hint}</span>}</label>
);

/** Upload every File in `items` ([{file, kind}]) in 2 MB chunks → refs the backend assembles. */
export async function uploadAll(items, setProgress) {
  const refs = [];
  for (let i = 0; i < items.length; i++) {
    setProgress({ name: items[i].file.name, i: i + 1, n: items.length, pct: 0 });
    const ref = await uploadInboxAttachment(legalUploadChunk, items[i].file, (pct) => setProgress((p) => ({ ...p, pct })));
    refs.push({ ...ref, kind: items[i].kind || "other" });
  }
  setProgress(null);
  return refs;
}

export function UploadProgress({ progress, testId }) {
  if (!progress) return null;
  return <div className="text-[10px] text-blue-700" data-testid={testId}>Uploading {progress.name} ({progress.i}/{progress.n}) — {progress.pct}%</div>;
}

export function FileDrop({ files, setFiles, busy, hint, testId, accept, multiple = true, label = "Add file", required = false }) {
  const ref = useRef(null);
  const add = (list) => {
    const incoming = Array.from(list || []);
    setFiles((cur) => {
      const next = incoming.filter((f) => !cur.some((x) => x.name === f.name && x.size === f.size));
      return multiple ? [...cur, ...next] : next.slice(0, 1);
    });
  };
  return (
    <div className={`border rounded p-2 ${required && files.length === 0 ? "bg-rose-50/40 border-rose-200" : "bg-slate-50/60"}`} data-testid={testId}
         onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}>
      <div className="flex items-center gap-2 flex-wrap">
        <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-btn`}>
          <Paperclip className="w-3 h-3 mr-1" />{label}
        </Button>
        <input ref={ref} type="file" multiple={multiple} accept={accept} className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid={`${testId}-input`} />
        <span className="text-[10px] text-muted-foreground">{files.length ? `${files.length} file${files.length === 1 ? "" : "s"}` : hint}</span>
      </div>
      {files.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="inline-flex items-center gap-1 border rounded bg-white px-2 py-0.5 text-[11px] max-w-[260px]" data-testid={`${testId}-file-${i}`}>
              <span className="truncate">{f.name}</span><span className="text-slate-400">{fmtSize(f.size)}</span>
              <button type="button" className="text-slate-400 hover:text-red-600" onClick={() => setFiles(files.filter((_, j) => j !== i))} disabled={busy} data-testid={`${testId}-remove-${i}`}><X className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CourtField({ court, onChange, testId = "petition-court", hint }) {
  const [picking, setPicking] = useState(!court);
  return (
    <div className="border rounded p-2 space-y-1.5" data-testid={testId}>
      <div className="flex items-start gap-2">
        <Landmark className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          {court ? (
            <>
              <div className="text-xs font-medium truncate" data-testid={`${testId}-name`}>{court.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">{formatCourtAddress(court)}{court.dx_number ? ` · ${court.dx_number}` : ""}</div>
            </>
          ) : <div className="text-xs text-rose-700" data-testid={`${testId}-missing`}>No court picked yet</div>}
          {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
        </div>
        <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setPicking((p) => !p)} data-testid={`${testId}-change`}>{picking ? "Done" : court ? "Change" : "Find court"}</Button>
      </div>
      {picking && <div className="pt-1" data-testid={`${testId}-picker`}><CourtPicker compact onSelect={(c) => { onChange(c); setPicking(false); }} /></div>}
    </div>
  );
}
